/**
 * Admin Service for Devimana Calendar App
 * Handles admin authentication and admin-only operations
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class AdminService {
  constructor() {
    this.adminToken = localStorage.getItem('admin_token');
  }

  /**
   * Admin login
   */
  async login(username, password) {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.adminToken = data.access_token;
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_username', data.admin_username);
        return { success: true, data };
      }

      return { success: false, error: data.message || 'Login failed' };
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Admin logout
   */
  logout() {
    this.adminToken = null;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
  }

  /**
   * Check if admin is logged in
   */
  isLoggedIn() {
    return !!this.adminToken;
  }

  /**
   * Get admin username
   */
  getAdminUsername() {
    return localStorage.getItem('admin_username');
  }

  /**
   * Get authorization headers
   */
  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.adminToken}`,
    };
  }

  /**
   * Get all users
   */
  async getAllUsers(skip = 0, limit = 100) {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/users?skip=${skip}&limit=${limit}`,
        {
          headers: this.getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Perform action on user (enable, disable, delete)
   */
  async performUserAction(userId, action) {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/action`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, action }),
      });

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error performing user action:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Create notification
   */
  async createNotification(notificationData) {
    try {
      const response = await fetch(`${API_URL}/api/admin/notifications/create`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(notificationData),
      });

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get notification history
   */
  async getNotificationHistory(skip = 0, limit = 50) {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/notifications/history?skip=${skip}&limit=${limit}`,
        {
          headers: this.getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching notification history:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get notification delivery status
   */
  async getNotificationStatus(notificationId) {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/notifications/${notificationId}/status`,
        {
          headers: this.getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching notification status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (response.status === 401) {
        this.logout();
        throw new Error('Admin session expired');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
const adminService = new AdminService();
export default adminService;
